import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { Upload, Loader2, Search, AlertTriangle, Trash2, Zap, Flame, FileDown, Pencil, CheckCircle, TrendingDown, TrendingUp, RefreshCw, FolderOpen, ChevronDown, ChevronRight } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// LUCE e GAS → Simulazione offerta (backend: routes/luce_gas.py sezione SIMULAZIONE e
// utils/simulazione_offerte.py). CTE in PDF letta dall'AI locale; bolletta da Fatture estrazione;
// confronto annuo della sola parte venditore, tutti i valori correggibili; PDF per il cliente.

const CAMPI_CTE = [
  ['fornitore', 'Fornitore'], ['nome_offerta', 'Offerta'], ['codice_offerta', 'Codice offerta'],
  ['tipo_utenza', 'Luce / gas', 'scelta', [['luce', 'LUCE'], ['gas', 'GAS']]],
  ['clienti', 'Clienti', 'scelta', [['domestici', 'DOMESTICI'], ['non_domestici', 'NON DOMESTICI'], ['tutti', 'TUTTI']]],
  ['tipo_prezzo', 'Prezzo', 'scelta', [['variabile', 'VARIABILE'], ['fisso', 'FISSO']]],
  ['indice_riferimento', 'Indice'], ['spread', 'Spread €/u', 'num'],
  ['prezzo_f0', 'Fisso F0 €/kWh', 'num'], ['prezzo_f1', 'Fisso F1 €/kWh', 'num'], ['prezzo_f2', 'Fisso F2 €/kWh', 'num'],
  ['prezzo_f3', 'Fisso F3 €/kWh', 'num'], ['prezzo_gas', 'Fisso gas €/Smc', 'num'],
  ['applica_perdite', 'Prezzi al lordo delle perdite', 'bool'], ['perdite_rete', 'Perdite (0,10 = 10%)', 'num'],
  ['quota_fissa', 'Quota fissa €', 'num'],
  ['quota_fissa_unita', 'Quota fissa per', 'scelta', [['anno', 'ANNO'], ['mese', 'MESE'], ['giorno', 'GIORNO']]],
  ['quota_potenza', 'Quota potenza vendita €/kW', 'num'],
  ['quota_potenza_unita', 'Quota potenza per', 'scelta', [['', '—'], ['anno', 'ANNO'], ['mese', 'MESE']]],
  ['altri_unitari', 'Altri corrispettivi €/u', 'num'], ['valida_da', 'Valida dal', 'date'], ['valida_a', 'Valida al', 'date'],
  ['durata_mesi', 'Durata mesi', 'num'], ['sconti', 'Sconti / bonus'],
];

const eur = (v) => (v === null || v === undefined ? '—'
  : v.toLocaleString('it-IT', { style: 'currency', currency: 'EUR' }));
const dataIt = (s) => (s ? `${s.slice(8, 10)}/${s.slice(5, 7)}/${s.slice(0, 4)}` : '');
const numero = (v) => (v === '' || v === null || v === undefined ? null : Number(String(v).replace(',', '.')));

const descriviCte = (d) => {
  if (!d) return '';
  const u = d.tipo_utenza === 'gas' ? 'Smc' : 'kWh';
  if (d.tipo_prezzo === 'variabile') return `${d.indice_riferimento || 'INDICE'} + ${d.spread ?? '?'} €/${u}`;
  const p = d.tipo_utenza === 'gas' ? d.prezzo_gas : (d.prezzo_f0 ?? d.prezzo_f1);
  return `FISSO ${p ?? '?'} €/${u}`;
};

// campo numerico che accetta la virgola e restituisce un numero (o null)
function CampoNum({ label, value, onChange, step = 'any', hint }) {
  const [testo, setTesto] = useState(value ?? '');
  useEffect(() => { if (numero(testo) !== value) setTesto(value ?? ''); }, [value]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div>
      <Label className="text-xs text-slate-500">{label}</Label>
      <Input inputMode="decimal" step={step} value={testo} className="h-8"
        onChange={(e) => { setTesto(e.target.value); const n = numero(e.target.value); onChange(Number.isNaN(n) ? null : n); }} />
      {hint && <p className="text-[11px] text-slate-400 mt-0.5">{hint}</p>}
    </div>
  );
}

function ColonnaOfferta({ titolo, o, onChange, luce }) {
  const u = luce ? 'kWh' : 'Smc';
  const set = (k) => (v) => onChange({ ...o, [k]: v });
  return (
    <div className="border border-slate-200 rounded-lg p-3 space-y-2">
      <div className="text-xs font-semibold text-slate-500">{titolo}</div>
      {o.nota && <p className="text-[11px] text-amber-700">{o.nota}</p>}
      <Input value={o.etichetta || ''} className="h-8 font-medium uppercase" onChange={(e) => onChange({ ...o, etichetta: e.target.value.toUpperCase() })} />
      <div className="flex gap-2">
        {['variabile', 'fisso'].map((t) => (
          <Button key={t} type="button" size="sm" variant={o.tipo_prezzo === t ? 'default' : 'outline'} onClick={() => onChange({ ...o, tipo_prezzo: t })}>
            {t.toUpperCase()}
          </Button>
        ))}
      </div>
      <div className="grid grid-cols-2 gap-2">
        {o.tipo_prezzo === 'variabile' ? (
          <CampoNum label={`Spread €/${u}`} value={o.spread} onChange={set('spread')} />
        ) : luce ? (
          <>
            <CampoNum label="Prezzo F0 / medio €/kWh" value={o.prezzo_f0} onChange={set('prezzo_f0')} />
            <CampoNum label="F1 €/kWh" value={o.prezzo_f1} onChange={set('prezzo_f1')} />
            <CampoNum label="F2 €/kWh" value={o.prezzo_f2} onChange={set('prezzo_f2')} />
            <CampoNum label="F3 €/kWh" value={o.prezzo_f3} onChange={set('prezzo_f3')} />
          </>
        ) : (
          <CampoNum label="Prezzo €/Smc" value={o.prezzo_gas} onChange={set('prezzo_gas')} />
        )}
        <CampoNum label={`Altri corrispettivi €/${u}`} value={o.altri_unitari} onChange={set('altri_unitari')} hint={luce ? 'es. sbilanciamento' : ''} />
        <CampoNum label="Quota fissa €/mese" hint="= annua ÷ 12"
          value={o.quota_fissa_annua == null ? null : Math.round((o.quota_fissa_annua / 12) * 10000) / 10000}
          onChange={(v) => onChange({ ...o, quota_fissa_annua: v == null ? null : Math.round(v * 12 * 1e6) / 1e6 })} />
        <CampoNum label="Quota fissa €/anno" value={o.quota_fissa_annua} onChange={set('quota_fissa_annua')} />
        {luce && <CampoNum label="Quota potenza vendita €/kW/anno" value={o.quota_potenza_annua} onChange={set('quota_potenza_annua')} />}
      </div>
      {luce && (
        <div className="flex flex-col gap-1 text-sm">
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={!!o.applica_perdite} onChange={(e) => onChange({ ...o, applica_perdite: e.target.checked })} />
            Prezzo applicato al lordo delle perdite di rete
          </label>
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={!!o.aggiungi_dispacciamento} onChange={(e) => onChange({ ...o, aggiungi_dispacciamento: e.target.checked })} />
            Aggiungi dispacciamento (togli se il prezzo lo comprende già)
          </label>
        </div>
      )}
    </div>
  );
}

export default function SimulazioneOfferta() {
  const [offerte, setOfferte] = useState([]);
  const [caricando, setCaricando] = useState(0);
  const [totaleCaricando, setTotaleCaricando] = useState(0);
  const [offertaSel, setOffertaSel] = useState('');
  const [migliori, setMigliori] = useState(null);
  const [mostraIndici, setMostraIndici] = useState(false);
  const [mostraScadute, setMostraScadute] = useState(false);
  const [modificaCte, setModificaCte] = useState(null);
  const [cercaBolletta, setCercaBolletta] = useState('');
  const [bollette, setBollette] = useState([]);
  const [bollettaSel, setBollettaSel] = useState(null);
  const [sim, setSim] = useState(null);
  const [risultato, setRisultato] = useState(null);
  const [salvando, setSalvando] = useState(false);
  const [salvate, setSalvate] = useState([]);
  const calcoloRef = useRef(0);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // ?bolletta=ID (da Scadenze): bolletta già scelta
  useEffect(() => {
    const id = searchParams.get('bolletta');
    if (!id) return;
    axios.get(`${API}/luce-gas/bollette/${id}`).then((r) => {
      setBollettaSel(r.data);
      setBollette((l) => (l.some((b) => b.id === r.data.id) ? l : [r.data, ...l]));
    }).catch(() => {});
  }, [searchParams]);

  const creaPratica = async (s) => {
    try {
      const r = await axios.post(`${API}/luce-gas/pratiche/da-simulazione`, { simulazione_id: s.id });
      navigate(`/luce-gas/pratiche?apri=${r.data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore');
    }
  };
  const [indici, setIndici] = useState([]);
  const [aggiornandoIndici, setAggiornandoIndici] = useState(false);
  const [modificaIndice, setModificaIndice] = useState(null);

  const fetchOfferte = useCallback(() => {
    axios.get(`${API}/luce-gas/offerte`, { params: { scadute: mostraScadute } })
      .then((r) => setOfferte(r.data || [])).catch(() => toast.error('Errore nel caricamento delle offerte'));
  }, [mostraScadute]);
  const fetchSalvate = useCallback(() => {
    axios.get(`${API}/luce-gas/simulazioni`).then((r) => setSalvate(r.data || [])).catch(() => {});
  }, []);
  const fetchIndici = useCallback(() => {
    axios.get(`${API}/luce-gas/indici`).then((r) => setIndici(r.data || [])).catch(() => {});
  }, []);
  useEffect(() => { fetchOfferte(); fetchSalvate(); fetchIndici(); }, [fetchOfferte, fetchSalvate, fetchIndici]);

  const aggiornaIndici = async () => {
    setAggiornandoIndici(true);
    try {
      const r = await axios.post(`${API}/luce-gas/indici/aggiorna`, {}, { timeout: 600000 });
      toast.success(r.data.aggiunti?.length ? `Aggiunti: ${r.data.aggiunti.join(', ')}` : 'Indici già aggiornati');
      if (r.data.avviso) toast.warning(r.data.avviso, { duration: 15000 });
      fetchIndici();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Aggiornamento non riuscito');
    } finally {
      setAggiornandoIndici(false);
    }
  };

  const salvaIndice = async () => {
    const { mese, pun_f1, pun_f2, pun_f3, psv_smc } = modificaIndice;
    if (!/^\d{4}-(0[1-9]|1[0-2])$/.test(mese || '')) { toast.error('Mese nel formato AAAA-MM'); return; }
    try {
      await axios.put(`${API}/luce-gas/indici/${mese}`, { pun_f1, pun_f2, pun_f3, psv_smc });
      setModificaIndice(null);
      fetchIndici();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore');
    }
  };

  const scegliIndici = (chiave) => setSim((s) => {
    const { etichetta, ...valori } = s.indici_opzioni[chiave];
    return { ...s, indici_scelta: chiave, parametri: { ...s.parametri, ...valori, fonte_indici: etichetta } };
  });

  useEffect(() => {
    const t = setTimeout(() => {
      axios.get(`${API}/luce-gas/bollette`, { params: { search: cercaBolletta, page_size: 30 } })
        .then((r) => setBollette((r.data.items || []).filter((b) => b.dati && b.dati.tipo_utenza)))
        .catch(() => {});
    }, 300);
    return () => clearTimeout(t);
  }, [cercaBolletta]);

  // bolletta scelta: tutte le offerte simulate, le 5 migliori e la prima scelta da sola
  useEffect(() => {
    if (!bollettaSel) { setMigliori(null); return undefined; }
    let attivo = true;
    setMigliori(null);
    setOffertaSel(''); // nessuna simulazione finché non si sa quali offerte sono proponibili
    axios.post(`${API}/luce-gas/simulazioni/migliori`, { bolletta_id: bollettaSel.id })
      .then((r) => {
        if (!attivo) return;
        setMigliori(r.data);
        setOffertaSel(r.data.migliori.length ? r.data.migliori[0].offerta_id : '');
      })
      .catch(() => { if (attivo) setMigliori({ migliori: [], escluse: {} }); });
    return () => { attivo = false; };
  }, [bollettaSel]);

  // con bolletta e offerta scelte: valori iniziali dal server
  useEffect(() => {
    if (!bollettaSel || !offertaSel) { setSim(null); setRisultato(null); return; }
    axios.post(`${API}/luce-gas/simulazioni/prepara`, { bolletta_id: bollettaSel.id, offerta_id: offertaSel })
      .then((r) => { const { risultato: ris, ...resto } = r.data; setSim(resto); setRisultato(ris); })
      .catch((e) => toast.error(e.response?.data?.detail || 'Errore nella preparazione'));
  }, [bollettaSel, offertaSel]);

  // ricalcolo a ogni modifica (calcolo sul server, uguale a quello del PDF)
  useEffect(() => {
    if (!sim) return undefined;
    const n = ++calcoloRef.current;
    const t = setTimeout(() => {
      axios.post(`${API}/luce-gas/simulazioni/calcola`, sim)
        .then((r) => { if (n === calcoloRef.current) setRisultato(r.data); })
        .catch(() => {});
    }, 350);
    return () => clearTimeout(t);
  }, [sim]);

  const caricaCte = async (files) => {
    const lista = Array.from(files || []);
    setTotaleCaricando(lista.length);
    for (let i = 0; i < lista.length; i += 1) {
      setCaricando(i + 1);
      const fd = new FormData();
      fd.append('file', lista[i]);
      try {
        const r = await axios.post(`${API}/luce-gas/offerte`, fd, { timeout: 600000 });
        setOffertaSel(r.data.id);
        if ((r.data.avvisi || []).length) toast.warning(`${lista[i].name}: alcuni valori da controllare`);
        else toast.success(`${lista[i].name}: CTE letta`);
      } catch (e) {
        toast.error(`${lista[i].name}: ${e.response?.data?.detail || 'lettura non riuscita'}`);
      }
    }
    setCaricando(0);
    fetchOfferte();
  };

  const salvaCte = async () => {
    try {
      await axios.put(`${API}/luce-gas/offerte/${modificaCte.id}`, { dati: modificaCte.dati });
      toast.success('Offerta salvata');
      setModificaCte(null);
      fetchOfferte();
      if (offertaSel === modificaCte.id && bollettaSel) setBollettaSel({ ...bollettaSel }); // rifà i valori iniziali
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore');
    }
  };

  const eliminaCte = async (o) => {
    if (!window.confirm(`Eliminare l'offerta ${o.dati?.nome_offerta || o.file_name}?`)) return;
    await axios.delete(`${API}/luce-gas/offerte/${o.id}`).catch(() => toast.error('Errore'));
    if (offertaSel === o.id) setOffertaSel('');
    fetchOfferte();
  };

  const apriPdf = async (id) => {
    try {
      const r = await axios.get(`${API}/luce-gas/simulazioni/${id}/pdf`, { responseType: 'blob' });
      window.open(URL.createObjectURL(r.data), '_blank');
    } catch (_) {
      toast.error('PDF non disponibile');
    }
  };

  const salvaSimulazione = async () => {
    setSalvando(true);
    try {
      const r = await axios.post(`${API}/luce-gas/simulazioni`, sim);
      toast.success('Simulazione salvata');
      fetchSalvate();
      apriPdf(r.data.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore nel salvataggio');
    } finally {
      setSalvando(false);
    }
  };

  const eliminaSalvata = async (s) => {
    if (!window.confirm('Eliminare questa simulazione?')) return;
    await axios.delete(`${API}/luce-gas/simulazioni/${s.id}`).catch(() => toast.error('Errore'));
    fetchSalvate();
  };

  const luce = sim?.parametri?.tipo_utenza !== 'gas';
  const setPar = (k) => (v) => setSim((s) => (k.startsWith('indice_')
    ? { ...s, indici_scelta: '', parametri: { ...s.parametri, [k]: v, fonte_indici: 'valori inseriti a mano' } }
    : { ...s, parametri: { ...s.parametri, [k]: v } }));
  const nomeBolletta = (b) => {
    const d = b.dati || {};
    return d.ragione_sociale || [d.cognome, d.nome].filter(Boolean).join(' ') || b.file_name;
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="simulazione-offerta-page">
      <div>
        <h1 className="text-3xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>Simulazione offerta</h1>
        <p className="text-slate-600 mt-1">Confronto annuo tra la bolletta del cliente e un'offerta (CTE), con PDF da consegnare</p>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* 1. BOLLETTA */}
        <Card className="border-slate-200">
          <CardContent className="p-4 space-y-3">
            <div className="font-semibold text-slate-800">1. Bolletta del cliente (da Fatture estrazione)</div>
            <div className="relative">
              <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-slate-400" />
              <Input className="pl-8" placeholder="Cerca cliente, POD/PDR, fornitore…" value={cercaBolletta} onChange={(e) => setCercaBolletta(e.target.value)} />
            </div>
            <div className="max-h-72 overflow-y-auto divide-y border rounded-lg">
              {bollette.length === 0 && <div className="p-3 text-sm text-slate-500">Nessuna bolletta letta</div>}
              {bollette.map((b) => {
                const d = b.dati || {};
                return (
                  <div key={b.id} onClick={() => setBollettaSel(b)}
                    className={`p-2 flex items-center gap-2 cursor-pointer ${bollettaSel?.id === b.id ? 'bg-amber-50 ring-1 ring-amber-300' : 'hover:bg-slate-50'}`}>
                    {d.tipo_utenza === 'gas' ? <Flame className="w-4 h-4 text-orange-500 shrink-0" /> : <Zap className="w-4 h-4 text-yellow-500 shrink-0" />}
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium whitespace-nowrap truncate">{nomeBolletta(b)}</div>
                      <div className="text-xs text-slate-500 truncate">
                        {d.fornitore} · {d.nome_offerta} · {d.consumo_annuo ?? '?'} {d.unita_consumo || ''}/anno · {dataIt(d.data_emissione)}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
        {/* 2. OFFERTE */}
        <Card className="border-slate-200">
          <CardContent className="p-4 space-y-3">
            <div className="font-semibold text-slate-800">2. Offerta (CTE)</div>
            {bollettaSel && (
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-2 space-y-1">
                <div className="text-xs font-semibold text-emerald-800">
                  {migliori === null ? 'Confronto con tutte le offerte…' : `LE MIGLIORI PER ${nomeBolletta(bollettaSel)}`}
                </div>
                {migliori === null && <Loader2 className="w-4 h-4 animate-spin text-emerald-700" />}
                {migliori && migliori.bloccante && migliori.migliori.length > 0 && (
                  <div className="text-xs text-red-700">Dati dell'offerta attuale incompleti: il risparmio si vede dopo averli inseriti nella simulazione.</div>
                )}
                {migliori && migliori.migliori.length === 0 && (
                  <div className="text-sm text-slate-600">Nessuna offerta proponibile a questa fornitura.</div>
                )}
                {migliori && migliori.migliori.map((m, i) => (
                  <button key={m.offerta_id} type="button" onClick={() => setOffertaSel(m.offerta_id)}
                    className={`w-full text-left rounded p-1.5 flex items-center gap-2 text-sm ${offertaSel === m.offerta_id ? 'bg-white ring-1 ring-emerald-400' : 'hover:bg-white'}`}>
                    <span className="font-bold text-emerald-700 w-5">{i + 1}.</span>
                    <span className="flex-1 truncate font-medium">{m.fornitore} — {m.nome_offerta}</span>
                    {(() => {
                      // differenza nel solo periodo della bolletta (di solito un mese); l'annua solo se mancano i mesi
                      const v = m.risparmio_periodo ?? m.risparmio_annuo;
                      if (v === null || v === undefined) return <span className="text-slate-400">—</span>;
                      const quando = m.risparmio_periodo == null ? "all'anno" : m.mesi_periodo > 1 ? `in ${m.mesi_periodo} mesi` : 'nel mese';
                      return (
                        <span className={`whitespace-nowrap font-semibold ${v > 0 ? 'text-emerald-700' : 'text-red-600'}`}>
                          {v > 0 ? `risparmio ${eur(v)}` : v < 0 ? `costa ${eur(-v)} in più` : 'stessa spesa'} {quando}
                        </span>
                      );
                    })()}
                  </button>
                ))}
                {migliori && Object.keys(migliori.escluse || {}).length > 0 && (
                  <div className="text-[11px] text-slate-500">
                    Escluse: {Object.entries(migliori.escluse).map(([k, n]) => `${n} ${k}`).join(' · ')}
                  </div>
                )}
              </div>
            )}
            <label className="flex flex-col items-center justify-center gap-1 border-2 border-dashed border-amber-300 bg-amber-50 rounded-lg py-4 text-amber-800 hover:bg-amber-100 cursor-pointer">
              {caricando ? <Loader2 className="w-5 h-5 animate-spin" /> : <Upload className="w-5 h-5" />}
              <span className="font-medium text-sm">{caricando ? `Lettura CTE ${caricando} di ${totaleCaricando}… (circa mezzo minuto l'una)` : 'Trascina qui uno o più PDF delle CTE o clicca per sceglierli'}</span>
              <input type="file" multiple accept="application/pdf" className="hidden" disabled={!!caricando}
                onChange={(e) => { caricaCte(e.target.files); e.target.value = ''; }} />
            </label>
            <label className="flex items-center gap-2 text-xs text-slate-500">
              <input type="checkbox" checked={mostraScadute} onChange={(e) => setMostraScadute(e.target.checked)} />
              mostra anche le offerte scadute (non vengono mai proposte)
            </label>
            <div className="max-h-72 overflow-y-auto divide-y border rounded-lg">
              {offerte.length === 0 && <div className="p-3 text-sm text-slate-500">Nessuna offerta valida caricata</div>}
              {offerte.map((o) => {
                const d = o.dati || {};
                const scaduta = d.valida_a && d.valida_a < new Date().toISOString().slice(0, 10);
                return (
                  <div key={o.id} onClick={() => { if (!scaduta) setOffertaSel(o.id); }}
                    title={scaduta ? 'Offerta scaduta: non si può proporre' : ''}
                    className={`p-2 flex items-center gap-2 ${scaduta ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'} ${offertaSel === o.id ? 'bg-amber-50 ring-1 ring-amber-300' : 'hover:bg-slate-50'}`}>
                    {d.tipo_utenza === 'gas' ? <Flame className="w-4 h-4 text-orange-500 shrink-0" /> : <Zap className="w-4 h-4 text-yellow-500 shrink-0" />}
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium truncate">{d.fornitore} — {d.nome_offerta}</div>
                      <div className="text-xs text-slate-500 flex flex-wrap gap-x-2">
                        <span>{descriviCte(d)}</span>
                        {d.quota_fissa != null && <span>· fisso {d.quota_fissa} €/{d.quota_fissa_unita || 'anno'}</span>}
                        {d.clienti && <span>· {d.clienti.replace('_', ' ').toUpperCase()}</span>}
                        {d.valida_a && <span className={scaduta ? 'text-red-600' : ''}>· fino al {dataIt(d.valida_a)}</span>}
                      </div>
                    </div>
                    {(o.avvisi || []).length > 0 && <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0" title="Valori da controllare" />}
                    <Button type="button" size="icon" variant="ghost" className="h-7 w-7" onClick={(e) => { e.stopPropagation(); setModificaCte({ ...o, dati: { ...o.dati } }); }}>
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                    <Button type="button" size="icon" variant="ghost" className="h-7 w-7 text-red-600" onClick={(e) => { e.stopPropagation(); eliminaCte(o); }}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

      </div>

      {/* 3. SIMULAZIONE */}
      {sim && (
        <Card className="border-slate-200">
          <CardContent className="p-4 space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <div className="font-semibold text-slate-800">3. Simulazione — {sim.cliente?.nome}</div>
                <div className="text-xs text-slate-500">{sim.cliente?.codice} · {sim.cliente?.indirizzo}</div>
              </div>
              <Button type="button" onClick={salvaSimulazione} disabled={salvando || (risultato?.bloccante ?? sim.bloccante)} className="bg-amber-600 hover:bg-amber-700">
                {salvando ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileDown className="w-4 h-4 mr-2" />}
                Salva e apri PDF per il cliente
              </Button>
            </div>

            {((risultato && risultato.avvisi_attuale) || sim.avvisi_attuale || []).map((a) => (
              <div key={a} className={`flex items-start gap-2 rounded-md border text-sm p-2 ${(risultato?.bloccante ?? sim.bloccante) ? 'bg-red-50 border-red-200 text-red-800' : 'bg-amber-50 border-amber-200 text-amber-800'}`}>
                <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" /> {a}
              </div>
            ))}
            {sim.parametri.nota_dispacciamento && <p className="text-xs text-slate-500">Dispacciamento {sim.parametri.nota_dispacciamento}.</p>}
            {sim.riscontro && (
              <p className={`text-xs ${Math.abs(sim.riscontro.calcolato - sim.riscontro.bolletta) / sim.riscontro.bolletta <= 0.02 ? 'text-emerald-700' : 'text-amber-700'}`}>
                Riscontro mese per mese con i valori della bolletta: calcolato {sim.riscontro.calcolato.toFixed(4)} €, in bolletta {sim.riscontro.bolletta.toFixed(4)} €
                {Math.abs(sim.riscontro.calcolato - sim.riscontro.bolletta) / sim.riscontro.bolletta <= 0.02 ? ' ✓ la formula torna' : ''}
              </p>
            )}
            {(sim.avvisi || []).map((a) => (
              <div key={a} className="flex items-start gap-2 rounded-md bg-red-50 border border-red-200 text-red-800 text-sm p-2">
                <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" /> {a}
              </div>
            ))}

            <div>
              <div className="text-xs font-semibold text-slate-500 mb-1">CONSUMI E INDICI (correggibili)</div>
              {Object.keys(sim.indici_opzioni || {}).length > 0 && (
                <div className="flex flex-wrap items-center gap-2 mb-2 text-sm">
                  <span className="text-slate-500">{luce ? 'PUN' : 'PSV'} da usare:</span>
                  {[['mese_bolletta', 'Mesi della bolletta (GME)'], ['bolletta', 'Stampati in bolletta'], ['ultimo_mese', 'Ultimo mese (GME)'], ['media_12', 'Media ultimi 12 mesi (GME)']]
                    .filter(([k]) => sim.indici_opzioni[k])
                    .map(([k, t]) => (
                      <Button key={k} type="button" size="sm" variant={sim.indici_scelta === k ? 'default' : 'outline'} onClick={() => scegliIndici(k)}>{t}</Button>
                    ))}
                  <span className="text-xs text-slate-400">{sim.parametri.fonte_indici}</span>
                </div>
              )}
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2">
                {luce && <CampoNum label="Consumo F1 kWh" value={sim.parametri.consumo_f1} onChange={setPar('consumo_f1')} />}
                {luce && <CampoNum label="Consumo F2 kWh" value={sim.parametri.consumo_f2} onChange={setPar('consumo_f2')} />}
                {luce && <CampoNum label="Consumo F3 kWh" value={sim.parametri.consumo_f3} onChange={setPar('consumo_f3')} />}
                <CampoNum label={`Consumo annuo ${luce ? 'kWh' : 'Smc'}`} value={sim.parametri.consumo_annuo} onChange={setPar('consumo_annuo')}
                  hint={luce ? 'usato se mancano le fasce' : ''} />
                {luce && <CampoNum label="Potenza kW" value={sim.parametri.potenza_kw} onChange={setPar('potenza_kw')} />}
                {luce ? (
                  <>
                    <CampoNum label="PUN F1 €/kWh" value={sim.parametri.indice_f1} onChange={setPar('indice_f1')} />
                    <CampoNum label="PUN F2 €/kWh" value={sim.parametri.indice_f2} onChange={setPar('indice_f2')} />
                    <CampoNum label="PUN F3 €/kWh" value={sim.parametri.indice_f3} onChange={setPar('indice_f3')} />
                    <CampoNum label="Perdite di rete (0,10 = 10%)" value={sim.parametri.perdite_rete} onChange={setPar('perdite_rete')} />
                    <CampoNum label="Dispacciamento €/kWh" value={sim.parametri.dispacciamento} onChange={setPar('dispacciamento')} hint="regolato ARERA, uguale per tutti" />
                  </>
                ) : (
                  <CampoNum label="PSV €/Smc" value={sim.parametri.indice_gas} onChange={setPar('indice_gas')} />
                )}
              </div>
            </div>

            {sim.parametri.nota_consumi && <p className="text-xs text-amber-700 -mt-2">Consumi F1/F2/F3: {sim.parametri.nota_consumi}</p>}
            <div className="grid md:grid-cols-2 gap-4">
              <ColonnaOfferta titolo="OFFERTA ATTUALE (dalla bolletta)" o={sim.attuale} luce={luce} onChange={(o) => setSim((s) => ({ ...s, attuale: o }))} />
              <ColonnaOfferta titolo="OFFERTA PROPOSTA (dalla CTE)" o={sim.proposta} luce={luce} onChange={(o) => setSim((s) => ({ ...s, proposta: o }))} />
            </div>

            {risultato && (risultato.bloccante ?? sim.bloccante) && (
              <div className="rounded-lg p-3 bg-red-50 text-red-800 font-medium">Completa i dati dell'offerta attuale indicati in rosso: senza, il confronto non è affidabile.</div>
            )}
            {risultato && !(risultato.bloccante ?? sim.bloccante) && (
              <div className="space-y-3">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm border border-slate-200 rounded">
                    <thead className="bg-slate-800 text-white">
                      <tr><th className="text-left p-2">Spesa annua di vendita</th><th className="text-right p-2">Attuale</th><th className="text-right p-2">Proposta</th></tr>
                    </thead>
                    <tbody>
                      {risultato.attuale.righe.map((r, i) => (
                        <tr key={r.fascia} className="border-t">
                          <td className="p-2 text-slate-600">{r.fascia}: {r.consumo?.toLocaleString('it-IT')} {luce ? 'kWh' : 'Smc'}</td>
                          <td className="p-2 text-right">{r.prezzo_unitario} €/u</td>
                          <td className="p-2 text-right">{risultato.proposta.righe[i]?.prezzo_unitario} €/u</td>
                        </tr>
                      ))}
                      <tr className="border-t"><td className="p-2">Energia</td><td className="p-2 text-right">{eur(risultato.attuale.energia)}</td><td className="p-2 text-right">{eur(risultato.proposta.energia)}</td></tr>
                      <tr className="border-t"><td className="p-2">Quota fissa</td><td className="p-2 text-right">{eur(risultato.attuale.quota_fissa)}</td><td className="p-2 text-right">{eur(risultato.proposta.quota_fissa)}</td></tr>
                      {(risultato.attuale.quota_potenza > 0 || risultato.proposta.quota_potenza > 0) && (
                        <tr className="border-t"><td className="p-2">Quota potenza</td><td className="p-2 text-right">{eur(risultato.attuale.quota_potenza)}</td><td className="p-2 text-right">{eur(risultato.proposta.quota_potenza)}</td></tr>
                      )}
                      <tr className="border-t bg-slate-50 font-bold"><td className="p-2">TOTALE ANNUO</td><td className="p-2 text-right">{eur(risultato.attuale.totale)}</td><td className="p-2 text-right">{eur(risultato.proposta.totale)}</td></tr>
                    </tbody>
                  </table>
                </div>
                {(risultato.mesi || []).length > 0 && (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm border border-slate-200 rounded">
                      <thead className="bg-slate-100 text-slate-700">
                        <tr>
                          <th className="text-left p-2">Spesa di vendita nel mese della bolletta</th>
                          <th className="text-right p-2">Consumo</th><th className="text-right p-2">Attuale</th>
                          <th className="text-right p-2">Proposta</th><th className="text-right p-2">Differenza</th>
                        </tr>
                      </thead>
                      <tbody>
                        {risultato.mesi.map((m) => (
                          <tr key={m.mese} className="border-t">
                            <td className="p-2">{['', 'Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno', 'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'][Number(m.mese.slice(5, 7))]} {m.mese.slice(0, 4)}</td>
                            <td className="p-2 text-right">{m.consumo.toLocaleString('it-IT')} {luce ? 'kWh' : 'Smc'}</td>
                            <td className="p-2 text-right">{eur(m.attuale.totale)}</td>
                            <td className="p-2 text-right">{eur(m.proposta.totale)}</td>
                            <td className={`p-2 text-right font-medium ${m.risparmio > 0 ? 'text-emerald-700' : 'text-red-600'}`}>{m.risparmio > 0 ? `-${eur(m.risparmio)}` : `+${eur(-m.risparmio)}`}</td>
                          </tr>
                        ))}
                        {risultato.mesi.length > 1 && (
                          <tr className="border-t bg-slate-50 font-bold">
                            <td className="p-2">TOTALE PERIODO</td>
                            <td className="p-2 text-right">{risultato.mesi.reduce((t, m) => t + m.consumo, 0).toLocaleString('it-IT')} {luce ? 'kWh' : 'Smc'}</td>
                            <td className="p-2 text-right">{eur(risultato.mesi.reduce((t, m) => t + m.attuale.totale, 0))}</td>
                            <td className="p-2 text-right">{eur(risultato.mesi.reduce((t, m) => t + m.proposta.totale, 0))}</td>
                            <td className={`p-2 text-right ${risultato.risparmio_periodo > 0 ? 'text-emerald-700' : 'text-red-600'}`}>{risultato.risparmio_periodo > 0 ? `-${eur(risultato.risparmio_periodo)}` : `+${eur(-risultato.risparmio_periodo)}`}</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                    <p className="text-[11px] text-slate-500 mt-1">Ogni mese con i suoi consumi e il PUN/PSV di quel mese; quota fissa = 1/12 dell'annua.</p>
                  </div>
                )}
                {(() => {
                  // come nel PDF: solo il periodo della bolletta (l'annuo solo se mancano i mesi)
                  const mesi = risultato.mesi || [];
                  const v = mesi.length ? risultato.risparmio_periodo : risultato.risparmio_annuo;
                  const attuale = mesi.reduce((t, m) => t + m.attuale.totale, 0);
                  const perc = mesi.length ? (attuale ? Math.round((v / attuale) * 1000) / 10 : null) : risultato.risparmio_percentuale;
                  const quando = !mesi.length ? "all'anno" : mesi.length === 1 ? 'nel mese' : `in ${mesi.length} mesi`;
                  return (
                    <div className={`flex flex-wrap items-center gap-3 rounded-lg p-3 ${v > 0 ? 'bg-emerald-50 text-emerald-800' : 'bg-red-50 text-red-800'}`}>
                      {v > 0 ? <TrendingDown className="w-6 h-6" /> : <TrendingUp className="w-6 h-6" />}
                      <span className="text-xl font-bold">
                        {v > 0
                          ? `Risparmio ${eur(v)} ${quando}${perc !== null ? ` (${String(perc).replace('.', ',')}%)` : ''}`
                          : `Nessun risparmio: la proposta costa ${eur(-v)} in più ${quando}`}
                      </span>
                    </div>
                  );
                })()}
                <p className="text-xs text-slate-500">
                  Solo la parte venditore: rete, oneri di sistema e imposte sono uguali con qualsiasi fornitore. Con offerte variabili l'indice è lo stesso per entrambe
                  e incide sul totale, non sulla differenza.
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* INDICI MENSILI */}
      <Card className="border-slate-200">
        <CardContent className="p-4 space-y-2">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <button type="button" className="font-semibold text-slate-800 flex items-center gap-1" onClick={() => setMostraIndici((v) => !v)}>
                {mostraIndici ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}Indici mensili (facoltativo)
              </button>
              <div className="text-xs text-slate-500">PUN Index GME per fascia e gas MGP-GAS (PSV) — fonte: Gestore dei Mercati Energetici. Si aggiornano da soli a inizio mese.</div>
            </div>
            {mostraIndici && <div className="flex gap-2">
              <Button type="button" size="sm" variant="outline" onClick={() => setModificaIndice({ mese: '', pun_f1: null, pun_f2: null, pun_f3: null, psv_smc: null })}>
                <Pencil className="w-4 h-4 mr-1" />Inserisci a mano
              </Button>
              <Button type="button" size="sm" variant="outline" onClick={aggiornaIndici} disabled={aggiornandoIndici}>
                {aggiornandoIndici ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1" />}Aggiorna dal GME
              </Button>
            </div>}
          </div>
          {mostraIndici && (
            <>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs text-slate-500 border-b">
                <tr>
                  <th className="text-left p-1.5">Mese</th><th className="text-right p-1.5">PUN F1</th><th className="text-right p-1.5">PUN F2</th>
                  <th className="text-right p-1.5">PUN F3</th><th className="text-right p-1.5">PUN medio</th><th className="text-right p-1.5">PSV €/MWh</th>
                  <th className="text-right p-1.5">PSV €/Smc</th><th className="text-left p-1.5">Fonte</th><th />
                </tr>
              </thead>
              <tbody>
                {indici.length === 0 && <tr><td colSpan={9} className="p-2 text-slate-500">Nessun indice: premi "Aggiorna dal GME"</td></tr>}
                {indici.slice(0, 12).map((r) => (
                  <tr key={r.mese} className="border-b last:border-0">
                    <td className="p-1.5 font-medium">{r.mese.slice(5, 7)}/{r.mese.slice(0, 4)}</td>
                    <td className="p-1.5 text-right">{r.pun_f1 ?? '—'}</td><td className="p-1.5 text-right">{r.pun_f2 ?? '—'}</td>
                    <td className="p-1.5 text-right">{r.pun_f3 ?? '—'}</td><td className="p-1.5 text-right">{r.pun_medio ?? '—'}</td>
                    <td className="p-1.5 text-right">{r.psv_mwh ?? '—'}</td><td className="p-1.5 text-right">{r.psv_smc ?? '—'}</td>
                    <td className="p-1.5 text-xs text-slate-500">{[r.fonte_pun, r.fonte_gas].filter(Boolean).join(' · ')}</td>
                    <td className="p-1.5 text-right">
                      <Button type="button" size="icon" variant="ghost" className="h-7 w-7" onClick={() => setModificaIndice({ ...r })}><Pencil className="w-3.5 h-3.5" /></Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-[11px] text-slate-400">Valori in €/kWh (PUN) e €/Smc (gas, con 0,01057275 MWh/Smc). PUN per fascia = media dei prezzi orari del mese nelle ore F1/F2/F3, come nelle CTE.</p>
            </>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!modificaIndice} onOpenChange={(o) => { if (!o) setModificaIndice(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Indice del mese</DialogTitle>
            <DialogDescription>Valori inseriti a mano: sostituiscono quelli del GME per quel mese</DialogDescription>
          </DialogHeader>
          {modificaIndice && (
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label className="text-xs text-slate-500">Mese (AAAA-MM)</Label>
                <Input className="h-8" value={modificaIndice.mese} onChange={(e) => setModificaIndice((m) => ({ ...m, mese: e.target.value.trim() }))} placeholder="2026-09" />
              </div>
              <CampoNum label="PUN F1 €/kWh" value={modificaIndice.pun_f1} onChange={(v) => setModificaIndice((m) => ({ ...m, pun_f1: v }))} />
              <CampoNum label="PUN F2 €/kWh" value={modificaIndice.pun_f2} onChange={(v) => setModificaIndice((m) => ({ ...m, pun_f2: v }))} />
              <CampoNum label="PUN F3 €/kWh" value={modificaIndice.pun_f3} onChange={(v) => setModificaIndice((m) => ({ ...m, pun_f3: v }))} />
              <CampoNum label="PSV €/Smc" value={modificaIndice.psv_smc} onChange={(v) => setModificaIndice((m) => ({ ...m, psv_smc: v }))} />
            </div>
          )}
          <div className="flex justify-end pt-2">
            <Button type="button" onClick={salvaIndice}><CheckCircle className="w-4 h-4 mr-2" />Salva</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* SIMULAZIONI SALVATE */}
      <Card className="border-slate-200">
        <CardContent className="p-4 space-y-2">
          <div className="font-semibold text-slate-800">Simulazioni salvate</div>
          {salvate.length === 0 && <div className="text-sm text-slate-500">Nessuna simulazione salvata</div>}
          <div className="divide-y">
            {salvate.map((s) => (
              <div key={s.id} className="py-2 flex items-center gap-3 text-sm">
                <span className="text-slate-500 w-24 shrink-0">{dataIt(s.created_at)}</span>
                <span className="font-medium whitespace-nowrap">{s.cliente?.nome}</span>
                <span className="text-slate-500 truncate flex-1">{s.attuale?.etichetta} → {s.proposta?.etichetta}</span>
                {(() => {
                  const mese = (s.risultato?.mesi || []).length > 0;
                  const v = (mese ? s.risultato.risparmio_periodo : s.risultato?.risparmio_annuo) || 0;
                  const suff = mese ? (s.risultato.mesi.length === 1 ? '/mese' : ` in ${s.risultato.mesi.length} mesi`) : '/anno';
                  return (
                    <Badge className={v > 0 ? 'bg-emerald-100 text-emerald-800' : 'bg-red-100 text-red-700'}>
                      {v > 0 ? `-${eur(v)}${suff}` : `+${eur(-v)}${suff}`}
                    </Badge>
                  );
                })()}
                <Button type="button" size="sm" variant="outline" onClick={() => apriPdf(s.id)}><FileDown className="w-4 h-4 mr-1" />PDF</Button>
                <Button type="button" size="sm" variant="outline" onClick={() => creaPratica(s)} title="Il cliente ha accettato: crea la pratica di cambio"><FolderOpen className="w-4 h-4 mr-1" />Pratica</Button>
                <Button type="button" size="icon" variant="ghost" className="h-8 w-8 text-red-600" onClick={() => eliminaSalvata(s)}><Trash2 className="w-4 h-4" /></Button>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* MODIFICA CTE */}
      <Dialog open={!!modificaCte} onOpenChange={(o) => { if (!o) setModificaCte(null); }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Dati dell'offerta</DialogTitle>
            <DialogDescription>{modificaCte?.file_name} — controlla i valori letti dall'AI</DialogDescription>
          </DialogHeader>
          {modificaCte && (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {CAMPI_CTE.map(([k, label, tipo, scelte]) => {
                const avviso = (modificaCte.avvisi || []).find((a) => a.campo === k);
                const val = modificaCte.dati[k];
                const set = (v) => setModificaCte((m) => ({ ...m, dati: { ...m.dati, [k]: v } }));
                return (
                  <div key={k} className={avviso ? 'rounded bg-amber-50 p-1' : ''}>
                    <Label className="text-xs text-slate-500">{label}</Label>
                    {tipo === 'scelta' ? (
                      <select className="w-full h-8 border rounded px-2 text-sm" value={val ?? ''} onChange={(e) => set(e.target.value)}>
                        {scelte.map(([v, t]) => <option key={v} value={v}>{t}</option>)}
                      </select>
                    ) : tipo === 'bool' ? (
                      <div className="h-8 flex items-center"><input type="checkbox" checked={!!val} onChange={(e) => set(e.target.checked)} /></div>
                    ) : tipo === 'num' ? (
                      <CampoNum label="" value={val} onChange={set} />
                    ) : (
                      <Input type={tipo === 'date' ? 'date' : 'text'} className={`h-8 ${tipo === 'date' ? '' : 'uppercase'}`} value={val ?? ''}
                        onChange={(e) => set(tipo === 'date' ? e.target.value : e.target.value.toUpperCase())} />
                    )}
                    {avviso && <p className="text-[11px] text-amber-700 mt-0.5">{avviso.motivo}</p>}
                  </div>
                );
              })}
            </div>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={async () => {
              const r = await axios.get(`${API}/luce-gas/offerte/${modificaCte.id}/file`, { responseType: 'blob' }).catch(() => null);
              if (r) window.open(URL.createObjectURL(r.data), '_blank');
            }}>Apri CTE</Button>
            <Button type="button" onClick={salvaCte}><CheckCircle className="w-4 h-4 mr-2" />Salva</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
